/**
 * ROUTE MANIFEST — the only file that knows where a lane's pages are mounted.
 *
 * Each feature module default-exports a component that renders its own <Routes> relative to
 * its base path, so a lane adds pages by editing files inside its own directory only. Nothing
 * here changes when a page is added.
 */
import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AppShell } from './shell/AppShell';
import { HomeIndex } from './home/HomeIndex';
import { RouteError } from './shell/RouteError';
import { RouteFallback } from './shell/RouteFallback';
import { NotFound } from './shell/NotFound';
import { TransportNavProbe } from '@/features/transport/nav-probe';
import { PLATFORM_ROUTES } from '@/features/apps/platform-routes';
import { AdminFrame } from '@/features/admin/AdminRail';

type FeatureModule = { default: ComponentType };

function feature(load: () => Promise<FeatureModule>) {
  const Lazy = lazy(load);
  return (
    <Suspense fallback={<RouteFallback />}>
      <Lazy />
    </Suspense>
  );
}

const routes: RouteObject[] = [
  {
    path: '/',
    // #214: the probe re-renders the shell once /api/transport/status answers (rail entry flag).
    element: <TransportNavProbe render={() => <AppShell />} />,
    errorElement: <RouteError />,
    children: [
      // R11 #649 (PRQ-08.1): the Apps portal at Expert; R-14 T-068: below it, the first app's home for the user's scope.
      { index: true, element: <HomeIndex /> },
      // One mount: the feature resolves `/planning` (pick a scope) and `/planning/:scopeId`.
      { path: 'planning/*', element: feature(() => import('@/features/planning/routes')) },
      { path: 'roadmap/*', element: feature(() => import('@/features/roadmap/routes')) },
      // R12 #716 (L2, PM 2026-10-16): "start from a source" is the document drop of Request a change now;
      // the old address opens it (the wow page stays in features/wow, unrouted, behind its flag).
      { path: 'studio/start', element: <Navigate to="/my-requests/new" replace /> },
      // R12 #716 (L2): the builder lives in the request; an old /studio link opens the owning request's Described stage.
      { path: 'studio/*', element: feature(() => import('@/features/requests/builder/LegacyRedirect')) },
      // R-14 T-069 T-091 T-089 (L4): the department's investment plan, My capabilities, Raise request.
      { path: 'department/*', element: feature(() => import('@/features/department/routes')) },
      { path: 'registry/*', element: feature(() => import('@/features/registry/routes')) },
      // #725 (PRQ-03): Products is retired — a product is the app's record now; old links land on the CIGS app's About page.
      { path: 'products/*', element: <Navigate to="/apps/cigs/about" replace /> },
      // G23 (#161): one Requests page — list, drawer/page, scoring as an action within it.
      { path: 'requests', element: feature(() => import('@/features/governance/requests/RequestsPage')) },
      {
        path: 'requests/:id',
        element: feature(() => import('@/features/governance/record/RequestRecordPage')),
      },
      { path: 'scoring', element: <Navigate to="/requests?state=scored" replace /> },
      // R11 #651: Studio, Products, Transport and proposals are in no menu; their routes below still
      // answer for deep links and the end-to-end suites.
      { path: 'governance/*', element: feature(() => import('@/features/governance/routes')) },
      // #728: every Admin page sits in the sectioned rail's frame
      { path: 'admin/*', element: <AdminFrame>{feature(() => import('@/features/admin/routes'))}</AdminFrame> },
      // G20 (#179, #180): the Readiness Map and the Map Builder.
      { path: 'service-domains/*', element: feature(() => import('@/features/service-domains/routes')) },
      // G21 (#190): the systems × capabilities matrix.
      { path: 'systems', element: feature(() => import('@/features/service-domains/SystemsMatrix')) },
      // G44 (#393): framework controls × obligations, with coverage.
      { path: 'controls', element: feature(() => import('@/features/controls/ControlsPage')) },
      // G26 (#214): change sets, queue, releases, imports, environments.
      { path: 'transport/*', element: feature(() => import('@/features/transport/routes')) },
      { path: 'library/*', element: feature(() => import('@/features/library/routes')) },
      { path: 'dashboard/*', element: feature(() => import('@/features/dashboard/routes')) },
      // R-16 T-127 (LH): Ask is a page in the content area, not a panel over another route.
      { path: 'ask', element: feature(() => import('@/features/ask/AskPage')) },
      // #613 #609 #624 #627: the platform's Apps portal, Approvals, My requests and the standard app pages.
      ...PLATFORM_ROUTES,
      // R11 #655: [App name] › App settings (L4's pages, web/src/features/app-admin).
      { path: 'apps/:slug/settings/*', element: feature(() => import('@/features/app-admin/routes')) },
      // R11 #662–#665 (L3): Data at platform level and inside each app; tabs come from the declarations.
      { path: 'data/*', element: feature(() => import('@/features/data/routes')) },
      { path: 'apps/:slug/data/*', element: feature(() => import('@/features/data/app-routes')) },
      // R-15 group B (L4): an app's Dashboard-type views, rendered from its declaration.
      { path: 'apps/:slug/views/:viewId', element: feature(() => import('@/features/dashboards/DashboardViewPage')) },
      // R17 T-208 (Lane T): the R&D Time Tracking app's own pages (my week, team time), declared on its module.
      { path: 'apps/:slug/time/*', element: feature(() => import('@/features/time-tracking/routes')) },
      // R12 #767 (L4): the Policy Register read view, opened from the Apps portal's Coming next card.
      { path: 'register/policies', element: feature(() => import('@/features/obligations/register/RegisterPage')) },
      // R17 T-148 (Lane B): the Frameworks browser — a framework's entries as a tree, and what maps to them.
      { path: 'frameworks/*', element: feature(() => import('@/features/frameworks/routes')) },
      // An unknown route is a 404 page, never a silent redirect (#130).
      { path: '*', element: <NotFound /> },
    ],
  },
];

// Created in the browser only, so the route table itself can be read by a test without a DOM.
export const router = (typeof document === 'undefined' ? null : createBrowserRouter(routes)) as ReturnType<typeof createBrowserRouter>;
export default routes;
